'use client';

import { useState } from 'react';
import { callServerAction } from '@/util/functions/fetching/callServerAction';
import { Quote } from '@/util/types/serverTypes';

type UseCheckExistingQuotesReturn = {
  checkQuotes: (contactId: string, propertyAddress?: string) => Promise<Quote[]>;
  loading: boolean;
  error: Error | null;
};

/**
 * Hook to check for existing quotes for a contact and optionally property
 * 
 * @param jwtToken - JWT token for authentication
 * @param companyId - Company ID for the request
 * @returns Object with checkQuotes function, loading state, and error
 */
export function useCheckExistingQuotes(
  jwtToken: string,
  companyId: string
): UseCheckExistingQuotesReturn {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const checkQuotes = async (
    contactId: string,
    propertyAddress?: string
  ): Promise<Quote[]> => {
    if (!contactId) {
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch both draft and abandoned quotes for this contact (both can be resumed)
      const quotes = await callServerAction<Quote[]>({
        url: 'quote',
        method: 'get',
        params: {
          contactId,
          status: ['draft', 'abandoned'], // Both statuses can be resumed
          populated: 'true',
          _companyId: companyId,
          limit: 20,
        },
        headers: {
          'client-token': jwtToken,
        },
      });

      // If property address provided, filter by matching property
      let filteredQuotes = (quotes || []);
      if (propertyAddress && filteredQuotes.length > 0) {
        // Normalize addresses for comparison
        const normalizeAddress = (addr: string) =>
          addr.toLowerCase().replace(/\s+/g, ' ').trim();
        const normalizedProperty = normalizeAddress(propertyAddress);

        filteredQuotes = filteredQuotes.filter((quote) => {
          if (!quote.property) return false;
          const quoteAddress = [
            quote.property.street,
            quote.property.city,
            quote.property.state,
            quote.property.zip,
          ]
            .filter(Boolean)
            .join(' ');
          return normalizeAddress(quoteAddress) === normalizedProperty;
        });
      }

      // Sort by most recently created
      const sortedQuotes = filteredQuotes.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA; // Most recent first
      });

      return sortedQuotes;
    } catch (err) {
      console.error('[useCheckExistingQuotes] Failed to check quotes:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to check quotes')
      );
      return [];
    } finally {
      setLoading(false);
    }
  };

  return {
    checkQuotes,
    loading,
    error,
  };
}

